import { CSRF_NAME } from "../../constants.js";
import { del, get } from "../../utils/idb.js";
import ElementController from "../base-controllers/ElementController.js";
import { getFormController } from "../components/form/lookup.js";
import { getOrPresentMessage } from "../global-controllers/messages/utils.js";
import { setFile } from "../utils/dom.js";
import { gettext } from "../utils/i18n.js";
import { dismissOverlay } from "../utils/overlay.js";
import { replaceHistory, visit } from "../utils/turbo.js";
export function inventoryDraft(form) {
    const draft = { __form_id: [form.id] };
    const values = getFormController(form)?.fieldsFormData || new FormData(form);
    for (const [name, value] of values) {
        if (typeof value === "string" && name !== CSRF_NAME)
            (draft[name] ||= []).push(value);
    }
    return draft;
}
/** Keep the existing inventory form, including other photos and entered fields. */
class InventoryPhotoHandoff extends ElementController {
    constructor() {
        super(...arguments);
        this.busy = false;
        this.usePhoto = async (event) => {
            const form = this.inventory;
            if (this.root.dataset.matchDialog) {
                const dialog = document.getElementById(this.root.dataset.matchDialog);
                if (dialog) {
                    const body = new FormData();
                    body.set("prefill", "1");
                    if (form)
                        body.set("draft", JSON.stringify(inventoryDraft(form)));
                    dialog._actionBody = body;
                }
                return;
            }
            if (this.root.hasAttribute("data-new-inventory")) {
                event.preventDefault();
                if (form && (this.root.hasAttribute("data-own-form") || this.matchesSource(form))) {
                    await getFormController(form)?.formReset();
                }
                const key = this.root.dataset.formStateKey;
                const sourceId = this.root.dataset.sourceGlobalId;
                if (key && sourceId) {
                    const data = (await get("form", key)) || [];
                    if (data.some((field) => field.name === "item_form-global_id" && field.value === sourceId)) {
                        await del("form", key);
                    }
                }
                visit(this.root.href);
                return;
            }
            if (!form || !this.root.dataset.photoUrl)
                return; // Ordinary navigation from the library.
            event.preventDefault();
            if (this.busy)
                return;
            this.busy = true;
            this.root.setAttribute("aria-busy", "true");
            try {
                const input = form.querySelector("input[type=file][name^=item_image_form_set-][name$=-image]");
                if (!input)
                    throw new Error("Image field unavailable");
                const previous = input.files?.[0];
                const url = new URL(this.root.dataset.photoUrl, location.origin);
                if (url.origin !== location.origin)
                    throw new Error("Photo must be same-origin");
                const response = await fetch(url, {
                    credentials: "same-origin", cache: "no-store", signal: AbortSignal.timeout(12000),
                });
                if (!response.ok)
                    throw new Error("Photo unavailable");
                const file = await response.blob();
                if (!file.type.startsWith("image/"))
                    throw new Error("Not an image");
                const dialog = this.root.closest("dialog");
                if (!this.root.isConnected || !form.isConnected || (dialog && !dialog.open))
                    return;
                if (input.files?.[0] !== previous)
                    throw new Error("A different photo was selected");
                const incoming = new File([file], "sokbild.jpg", { type: file.type });
                const controller = getFormController(form);
                if (controller) {
                    if (!await controller.applyIncomingFile(input, incoming, ".formset--item-image", () => !dialog || dialog.open))
                        return;
                }
                else {
                    if (previous)
                        throw new Error("Image formset unavailable");
                    setFile(input, incoming);
                }
                const query = form.querySelector("[name=library_photo_id]");
                if (query) {
                    query.value = this.root.dataset.photoId;
                    // The hidden field, submission URL and reload URL must describe the
                    // same source. Changing only the field leaves an older query in GET.
                    const locationUrl = new URL(location.href);
                    const actionUrl = new URL(form.action || location.href);
                    if (actionUrl.origin !== location.origin)
                        throw new Error("Inventory must be same-origin");
                    locationUrl.searchParams.set("image_query", query.value);
                    actionUrl.searchParams.set("image_query", query.value);
                    form.action = actionUrl.href;
                    replaceHistory(locationUrl.href);
                    query.dispatchEvent(new Event("change", { bubbles: true }));
                }
                if (dialog)
                    dismissOverlay(dialog);
                // Ordinary bound rerender refreshes source-specific suggestions without
                // auto-prefilling over the user's fields or losing live file inputs.
                if (controller)
                    await controller.sendForm({ rerender: true });
            }
            catch {
                await getOrPresentMessage("error", gettext("The image could not be retrieved. Your input remains. Try again."));
            }
            finally {
                this.busy = false;
                this.root.removeAttribute("aria-busy");
            }
        };
    }
    get inventory() {
        return document.querySelector("form[data-inventory-draft]");
    }
    matchesSource(form) {
        return Boolean(this.root.dataset.sourceGlobalId &&
            form.querySelector("[name=item_form-global_id]")?.value === this.root.dataset.sourceGlobalId);
    }
    init() {
        const form = this.inventory;
        if (!this.root.hasAttribute("data-completed") || !form || !this.matchesSource(form))
            return;
        const input = form.querySelector("[name=library_photo_id]");
        if (input) {
            input.value = this.root.dataset.photoId;
            input.dispatchEvent(new Event("change", { bubbles: true }));
        }
        const notice = document.querySelector("[data-inventory-complete-notice]");
        if (notice)
            notice.hidden = false;
        for (const button of document.querySelectorAll("button[type=submit]")) {
            if (button.form === form)
                button.disabled = true;
        }
    }
}
InventoryPhotoHandoff.id = "InventoryPhotoHandoff";
InventoryPhotoHandoff.binds = { root: [["click", "usePhoto"]] };
export default InventoryPhotoHandoff;
//# sourceMappingURL=InventoryPhotoHandoff.js.map